import { ReactNode } from 'react';
import { AppLink, Callout, Code, Ext, H2, H3, OL, P, Pre, UL } from './Docs';

/** Page registry — slug pair → content. The Docs shell looks the page
 * up by `${section}/${page}`. Keep prose tight and tied to the actual
 * Sendepot product (no marketing fluff, no fabricated features). */
const PAGES: Record<string, () => ReactNode> = {
  // ── Welcome ──────────────────────────────────────────────────────────
  'welcome/read-first': () => (
    <>
      <P>
        Sendepot creates Microsoft 365 sending infrastructure for cold-email
        operators. You bring tenants and a sequencer account; Sendepot
        provisions up to 150 mailboxes per tenant, configures DNS, sets
        passwords, and OAuths each mailbox into your sequencer. The
        mailboxes you end up with are exactly the same as those from
        any Outlook reseller — Sendepot just removes the middleman.
      </P>
      <Callout>
        We are not an inbox reseller. We provide the technology and
        automation; you keep ownership of every account.
      </Callout>
      <H2>What you'll need</H2>
      <UL>
        <li>An active Sendepot license — pick a plan during onboarding.</li>
        <li>One or more Microsoft 365 tenants you control. <AppLink to="/docs/microsoft-tenants/where-to-get">Four ways to source these</AppLink>.</li>
        <li>A sequencer account — Instantly and Smartlead are live; PlusVibe and EmailBison coming.</li>
        <li>One sending domain per tenant.</li>
      </UL>
      <H2>How Sendepot fits in</H2>
      <P>
        One tenant is one isolated Microsoft 365 environment with its own
        admin and its own ~100-inbox capacity. Sendepot drives Microsoft's
        admin console for you — adding the domain, generating mailboxes,
        verifying DNS, rolling passwords, then completing the OAuth
        handshake into your sequencer of choice. From "I have a tenant
        and a domain" to "your mailboxes are in your sequencer warming up" is
        usually 30–60 minutes per tenant, hands-free.
      </P>
      <H2>Where to go next</H2>
      <UL>
        <li><AppLink to="/docs/getting-started/quick-start">Quick start</AppLink> — sign up and run your first tenant in 10 minutes.</li>
        <li><AppLink to="/docs/agents/overview">Integrate with agents</AppLink> — use Sendepot from Claude or any MCP client.</li>
        <li><AppLink to="/docs/best-practices/reply-rates">Best practices</AppLink> — what actually moves reply rates in 2026.</li>
      </UL>
    </>
  ),

  // ── Getting started ─────────────────────────────────────────────────
  'getting-started/quick-start': () => (
    <>
      <P>You can be sending from a Sendepot-provisioned mailbox the same day — typically a few hours from "tenant + domain handed over" to "every mailbox connected" (25–150 per tenant, your choice). Most of that wait is DNS propagation.</P>
      <H2>1. Create an account</H2>
      <P>
        Head to <Ext href="https://sendepot.com/signup">sendepot.com/signup</Ext>{' '}
        and enter your email + company info. We email you a sign-in
        link; click it and you're in. No password to remember.
      </P>
      <H2>2. Connect your sequencer</H2>
      <P>
        In <AppLink to="/settings">Settings → Sequencer API keys</AppLink>,
        click <strong>Add a key</strong> next to your platform. For
        Smartlead you'll also need to paste your "Add Outlook account"
        OAuth URL — see the <AppLink to="/docs/sequencers/smartlead">Smartlead guide</AppLink>.
      </P>
      <H2>3. Add your first tenant</H2>
      <P>
        From <AppLink to="/tenants">Tenants</AppLink>, click{' '}
        <strong>+ New tenant</strong>. You'll need:
      </P>
      <UL>
        <li><strong>Tenant login</strong> — the Microsoft admin email (e.g. <Code>admin@mycorp.onmicrosoft.com</Code>).</li>
        <li><strong>Initial password</strong> — Sendepot will rotate this immediately.</li>
        <li><strong>Sending domain</strong> — the domain mailboxes will live on.</li>
        <li><strong>Sequencer</strong> — Instantly or Smartlead.</li>
      </UL>
      <P>
        Submit, and Sendepot starts running phases automatically. You'll
        see a progress bar climb as each phase completes.
      </P>
      <H2>4. Watch it run</H2>
      <P>
        The seven phases (DNS, mailbox creation, password rollout, OAuth, etc.)
        take 30–60 minutes total. Most of that is DNS propagation —
        you can leave the tab and come back.
      </P>
      <H2>5. Wait 24 hours, then send</H2>
      <P>
        New mailboxes need a 24-hour rest before warm-up. Don't start
        sending immediately or Microsoft will flag the burst — see{' '}
        <AppLink to="/docs/tenant-blocking/overview">tenant-level blocking</AppLink>.
      </P>
      <Callout kind="tip">
        Want to do all of the above from Claude or your terminal? The{' '}
        <AppLink to="/docs/agents/overview">MCP integration</AppLink>{' '}
        exposes the same tools for AI agents — "Claude, add a new tenant
        for example.com" works literally as a sentence.
      </Callout>
    </>
  ),

  'getting-started/concepts': () => (
    <>
      <P>Three things unlock Sendepot. Get these right and the rest is mechanical.</P>
      <H3>License</H3>
      <P>
        Your monthly subscription. Without an active license you can't
        place provisioning orders — manage it under{' '}
        <AppLink to="/settings">Settings → Billing</AppLink>.
      </P>
      <H3>Tenant</H3>
      <P>
        One Microsoft 365 tenant = one custom domain you can add = up to
        up to 150 mailboxes you can run on it (25 minimum, 150 max,
        100 default — you choose per tenant). You provide the tenant;
        Sendepot provisions the inboxes inside it. When you "create a tenant" in
        Sendepot, you're really registering an existing M365 tenant with
        us so we can drive its admin console on your behalf.
      </P>
      <H3>Mailbox</H3>
      <P>
        A single sending address inside a tenant. Sendepot generates
        permutations of the names you supply (e.g. <Code>jane.doe@</Code>,{' '}
        <Code>j.doe@</Code>, <Code>jdoe@</Code>) and creates them in
        bulk. Each mailbox gets a fresh password, a primary domain alias,
        and an OAuth handshake into your sequencer.
      </P>
      <H2>The math</H2>
      <Pre>{`1 tenant         = 1 sending domain
1 tenant         = 25–150 mailboxes (you pick; default 100)
goal: 1,000 mb   = 10 tenants + active license + sequencer key`}</Pre>
      <H2>What Sendepot does NOT include</H2>
      <UL>
        <li>The tenant itself — you bring it. <AppLink to="/docs/microsoft-tenants/where-to-get">How to source one</AppLink>.</li>
        <li>Your sequencer subscription — bring your own Instantly / Smartlead account.</li>
        <li>Warm-up — that runs inside your sequencer once mailboxes are connected.</li>
        <li>Cold-email copy or send — Sendepot stops at "mailbox is connected and ready."</li>
      </UL>
    </>
  ),

  'getting-started/pricing': () => (
    <>
      <P>
        Sendepot is a paid platform — three monthly tiers, all month-to-month,
        cancel any time. Pick one during signup. See{' '}
        <AppLink to="/settings">Settings → Billing</AppLink> for the current
        plan options and to manage your subscription.
      </P>
      <H2>Tiers</H2>
      <UL>
        <li><strong>Starter — $97/mo</strong>: up to 10 tenants (1,000 mailboxes).</li>
        <li><strong>Agency — $247/mo</strong>: up to 50 tenants (5,000 mailboxes).</li>
        <li><strong>Unlimited — $597/mo</strong>: unlimited tenants.</li>
      </UL>
      <H2>What you pay vs. what you bring</H2>
      <UL>
        <li><strong>Sendepot subscription</strong> — covers the platform.</li>
        <li><strong>Tenants</strong> — sourced from one of four channels (see <AppLink to="/docs/microsoft-tenants/where-to-get">Where to get them</AppLink>). Costs range from ~$1/mo (freelancer) to $12/mo (buy from us).</li>
        <li><strong>Domains</strong> — one per tenant, ~$3–10/year each, bought direct from any registrar.</li>
        <li><strong>Sequencer</strong> — Instantly / Smartlead pricing is independent.</li>
      </UL>
    </>
  ),

  // ── Integrate with agents ───────────────────────────────────────────
  'agents/overview': () => (
    <>
      <P>Sendepot is built API-first. You can drive it three ways:</P>
      <UL>
        <li><strong>Web UI</strong> — <Ext href="https://sendepot.com/tenants">sendepot.com/tenants</Ext>. Best for one-off operators.</li>
        <li><strong>MCP</strong> — JSON-RPC tool calls from any MCP-speaking agent (Claude Desktop, Claude Code, Cursor, ChatGPT custom GPTs). Best for "tell the agent what you want" workflows.</li>
        <li><strong>HTTP API</strong> — REST endpoints with bearer-token auth. Best for scripts, cron jobs, custom integrations.</li>
      </UL>
      <H2>Pick a starting point</H2>
      <UL>
        <li>Want to talk to Sendepot in plain English? → <AppLink to="/docs/agents/claude-desktop">Claude Desktop</AppLink>.</li>
        <li>Already use Claude Code? → <AppLink to="/docs/agents/claude-code">Claude Code</AppLink> (one command).</li>
        <li>Other MCP client? → <AppLink to="/docs/agents/cursor">Cursor & friends</AppLink>.</li>
        <li>Plain HTTP / curl / your own backend? → <AppLink to="/docs/agents/http-quickstart">HTTP API quickstart</AppLink>.</li>
      </UL>
      <H2>Endpoints at a glance</H2>
      <Pre>{`MCP server     POST  https://sendepot.com/mcp
REST API       any   https://sendepot.com/api/*
OpenAPI spec   GET   https://sendepot.com/api/openapi.json
Swagger UI           https://sendepot.com/api/docs`}</Pre>
      <P>
        Both surfaces use the same <Code>Authorization: Bearer ie_…</Code>{' '}
        token, scoped to your workspace. <AppLink to="/docs/agents/http-quickstart">Mint a token →</AppLink>
      </P>
    </>
  ),

  'agents/claude-desktop': () => (
    <>
      <P>
        Add Sendepot as a custom connector in Claude Desktop and you
        can ask Claude things like "list my failing tenants" or "create
        a tenant for example.com using the Smartlead key labelled 'main'."
        Same security model as the API — each call respects the token's
        scope and your workspace boundary.
      </P>
      <H2>1. Mint an API token</H2>
      <OL>
        <li>Sign in to <Ext href="https://sendepot.com">sendepot.com</Ext>.</li>
        <li>Open <AppLink to="/tokens">API tokens</AppLink> in the sidebar.</li>
        <li>Click <strong>+ New token</strong>. Pick scopes (<Code>read</Code> for inspection-only, <Code>write</Code> to allow Claude to create tenants).</li>
        <li>Copy the <Code>ie_…</Code> token — you can't view it again.</li>
      </OL>
      <H2>2. Add Sendepot as a custom connector</H2>
      <OL>
        <li>Open Claude Desktop → <strong>Settings</strong>.</li>
        <li>Go to <strong>Connectors</strong> → click <strong>Add custom connector</strong>.</li>
        <li>Set <strong>Server URL</strong> to <Code>https://sendepot.com/mcp</Code>.</li>
        <li>For auth, paste your <Code>ie_…</Code> token as the Bearer token.</li>
        <li>Save. The Sendepot tools will appear in the 🔧 menu next to the message composer.</li>
      </OL>
      <Callout>
        Claude Desktop supports remote HTTP MCP servers natively — no
        <Code>mcp-remote</Code> wrapper or local config file edits
        needed. If you're on an older Claude Desktop without the
        Connectors UI, update it first.
      </Callout>
      <H2>3. Try it</H2>
      <P>Ask Claude:</P>
      <Pre>{`> List all my Sendepot tenants and tell me which ones are failing.

> Create a new tenant for sendingdomain.com using
  admin@example.onmicrosoft.com and the Smartlead key labelled "main".

> Show me the latest logs for tenant <id>.`}</Pre>
      <P>
        See <AppLink to="/docs/agents/tools">MCP tool reference</AppLink>{' '}
        for everything Claude can do.
      </P>
    </>
  ),

  'agents/claude-code': () => (
    <>
      <P>
        Claude Code (the CLI) speaks MCP natively. One command and you're
        done — no JSON to edit.
      </P>
      <H2>1. Mint an API token</H2>
      <P>
        Open <AppLink to="/tokens">API tokens</AppLink> in the Sendepot
        sidebar, create a token with the scopes you want, copy the{' '}
        <Code>ie_…</Code> string.
      </P>
      <H2>2. Add Sendepot to Claude Code</H2>
      <Pre>{`claude mcp add --transport http sendepot \\
  https://sendepot.com/mcp \\
  --header "Authorization: Bearer ie_YOUR_TOKEN_HERE"`}</Pre>
      <P>
        Verify it registered:
      </P>
      <Pre>{`claude mcp list`}</Pre>
      <H2>3. Use it</H2>
      <P>
        Inside any Claude Code session, the <Code>sendepot</Code> tools
        show up automatically. Ask in plain English:
      </P>
      <Pre>{`> Use sendepot to list every failing tenant in my workspace
  and write a CSV with their domain, status, and last error.`}</Pre>
      <Callout kind="tip">
        Tokens are stored in Claude Code's config (<Code>~/.claude/</Code>).
        Rotate by running <Code>claude mcp remove sendepot</Code> followed
        by another <Code>claude mcp add</Code> with the new token.
      </Callout>
    </>
  ),

  'agents/cursor': () => (
    <>
      <P>
        Any MCP-speaking client can talk to Sendepot. The connection
        details are the same regardless of client:
      </P>
      <Pre>{`URL:           https://sendepot.com/mcp
Transport:     Streamable HTTP (a strict subset of MCP)
Auth header:   Authorization: Bearer ie_YOUR_TOKEN_HERE
Protocol:      MCP 2024-11-05`}</Pre>
      <H2>Cursor</H2>
      <P>
        Open Cursor → <strong>Settings</strong> → <strong>MCP</strong>{' '}
        → <strong>+ Add new MCP server</strong>. Cursor supports remote
        HTTP servers natively — paste this JSON:
      </P>
      <Pre>{`{
  "mcpServers": {
    "sendepot": {
      "url": "https://sendepot.com/mcp",
      "headers": {
        "Authorization": "Bearer ie_YOUR_TOKEN_HERE"
      }
    }
  }
}`}</Pre>
      <H2>ChatGPT custom GPTs / Actions</H2>
      <P>
        ChatGPT Actions don't use MCP yet — they consume OpenAPI. Use
        the REST surface instead: import{' '}
        <Ext href="https://sendepot.com/api/openapi.json">sendepot.com/api/openapi.json</Ext>{' '}
        as the action schema, set the auth type to "API Key" with{' '}
        <Code>Authorization</Code> header value <Code>Bearer ie_…</Code>.
      </P>
      <H2>Generic MCP testing</H2>
      <P>
        The MCP Inspector (<Ext href="https://github.com/modelcontextprotocol/inspector">github.com/modelcontextprotocol/inspector</Ext>)
        is the easiest way to verify the server is up and explore the
        tool surface without a full client.
      </P>
    </>
  ),

  'agents/http-quickstart': () => (
    <>
      <P>
        Five minutes from zero to "I created a tenant from curl." The
        REST surface and the MCP surface share the same auth + scope
        model — anything you can do here, an MCP agent can do too.
      </P>
      <H2>1. Mint a token</H2>
      <OL>
        <li>Open <AppLink to="/tokens">API tokens</AppLink>.</li>
        <li>Click <strong>+ New token</strong>, give it a label, pick scopes.</li>
        <li>Copy the <Code>ie_…</Code> string. It's shown once.</li>
      </OL>
      <H2>2. Verify auth</H2>
      <Pre>{`curl https://sendepot.com/api/me \\
  -H "Authorization: Bearer ie_YOUR_TOKEN_HERE"

# {"authenticated": true, "is_api_token": true, ...}`}</Pre>
      <H2>3. List tenants</H2>
      <Pre>{`curl https://sendepot.com/api/tenants \\
  -H "Authorization: Bearer ie_YOUR_TOKEN_HERE"`}</Pre>
      <H2>4. Create a tenant</H2>
      <Pre>{`curl -X POST https://sendepot.com/api/tenants \\
  -H "Authorization: Bearer ie_YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ga_upn": "admin@example.onmicrosoft.com",
    "ga_password": "<initial password>",
    "target_domain": "example.com",
    "sequencer": "instantly"
  }'`}</Pre>
      <P>
        The response gives you the tenant ID. Provisioning starts
        immediately — poll <Code>GET /api/tenants/&lt;id&gt;</Code> to
        watch progress.
      </P>
      <H2>Scopes</H2>
      <UL>
        <li><Code>read</Code> — list, get, status. Inspection-only.</li>
        <li><Code>write</Code> — create tenants, run phases, add sequencer keys.</li>
        <li><Code>secrets</Code> — operator-cookie only. Cannot be granted to a token by design.</li>
      </UL>
      <H2>Full reference</H2>
      <P>
        Every endpoint, request shape, and response schema lives at{' '}
        <Ext href="https://sendepot.com/api/docs">sendepot.com/api/docs</Ext>{' '}
        (Swagger UI, auto-generated from the OpenAPI spec).
      </P>
    </>
  ),

  'agents/tools': () => (
    <>
      <P>
        Sendepot's MCP server exposes the same primitives as the REST
        API as JSON-RPC tools. Below is the full surface as of the
        current release; <Code>tools/list</Code> on the live server is
        always authoritative.
      </P>
      <H2>Read</H2>
      <UL>
        <li><Code>list_tenants</Code> — list tenants visible to the caller.</li>
        <li><Code>get_tenant</Code> — full tenant + mailbox state by id or domain.</li>
        <li><Code>list_clients</Code> — list workspaces (admins see all).</li>
        <li><Code>list_sequencer_keys</Code> — sequencer accounts on a workspace.</li>
        <li><Code>list_mailboxes</Code> — mailboxes for a tenant.</li>
        <li><Code>queue_status</Code> — counts of tenants by status.</li>
      </UL>
      <H2>Write</H2>
      <UL>
        <li><Code>create_tenant</Code> — onboard a new M365 tenant. Pin to a sequencer key by id or label.</li>
        <li><Code>add_sequencer_key</Code> — register an Instantly or Smartlead key. For Smartlead, also pass <Code>oauth_url</Code>.</li>
      </UL>
      <Callout>
        Phase retries are automatic — every phase auto-retries up to 3
        times with backoff. There's no <Code>run_pipeline</Code> tool;
        if a tenant is genuinely stuck after the auto-retry budget is
        spent, restart it from the web app's tenant detail view.
      </Callout>
      <H2>Secrets (operator session only)</H2>
      <UL>
        <li><Code>lookup_secret</Code> — decrypt a tenant secret. Cookie session only — bearer tokens cannot reach this tool.</li>
      </UL>
      <Callout kind="tip">
        Each tool is checked against your token's scopes server-side. If
        a tool is hidden in <Code>tools/list</Code>, you don't have the
        scope for it.
      </Callout>
      <H2>Discovering live</H2>
      <P>
        Hit <Code>POST /mcp</Code> with the standard MCP discovery call:
      </P>
      <Pre>{`curl -X POST https://sendepot.com/mcp \\
  -H "Authorization: Bearer ie_YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`}</Pre>
      <P>
        Each tool response includes its <Code>inputSchema</Code> so
        agents can introspect arguments without docs.
      </P>
    </>
  ),

  // ── Microsoft tenants ───────────────────────────────────────────────
  'microsoft-tenants/what-they-are': () => (
    <>
      <P>
        A Microsoft tenant is the isolated instance you get when you
        subscribe to Microsoft 365 business plans. It comes with an
        admin control panel, the ability to create user mailboxes, a
        default <Code>yourname@yourcompany.onmicrosoft.com</Code>{' '}
        address, and permission to attach a custom domain.
      </P>
      <H2>Don't overthink it</H2>
      <Pre>{`1 tenant   ≈ $4.75–12/month       (depends on source)
1 tenant   = 1 custom domain
1 tenant   = 25–150 mailboxes (you pick; default 100)`}</Pre>
      <P>
        For each tenant you give Sendepot, we add the domain, generate
        the mailboxes, configure DNS, and OAuth them into your sequencer.
        Provisioning is fully managed end-to-end.
      </P>
      <H2>What we need from a tenant</H2>
      <UL>
        <li>The admin login — usually <Code>admin@&lt;something&gt;.onmicrosoft.com</Code>.</li>
        <li>The current admin password (we rotate it on first run).</li>
        <li>A custom domain you want mailboxes on.</li>
      </UL>
      <P>
        Whichever sourcing channel you use (<AppLink to="/docs/microsoft-tenants/where-to-get">see options</AppLink>),
        the deliverable is the same: that triple of admin email +
        password + domain.
      </P>
    </>
  ),

  'microsoft-tenants/where-to-get': () => (
    <>
      <P>Four channels. Listed roughly best-to-worst for someone starting out.</P>
      <H2>1. Buy from us — $12/month</H2>
      <UL>
        <li>Email <Ext href="mailto:support@sendepot.com">support@sendepot.com</Ext>; tenants delivered within 24 hours.</li>
        <li>Includes support, replacements if Microsoft kills one, no setup work on your end.</li>
        <li>Best for your first 10 tenants while you figure out the workflow.</li>
      </UL>
      <H2>2. Microsoft Partner / CSP — ~$4.75/month</H2>
      <UL>
        <li>Cheapest at scale; create tenants in 15 minutes once approved.</li>
        <li>Approval is 1–4 weeks. Pax8 is the fastest path — see <AppLink to="/docs/microsoft-tenants/become-a-partner">Becoming a partner</AppLink>.</li>
        <li>Diversify across multiple CSPs so a single revoked partnership doesn't kill all your tenants.</li>
      </UL>
      <H2>3. Direct from Microsoft — ~$4.75/month</H2>
      <UL>
        <li>Sign up for Microsoft 365 Business Standard with your own card.</li>
        <li>Requires phone verification + a real address; high fraud-detection failure rate when scaling.</li>
        <li>4–8 hours per tenant. Realistic for 1–3 tenants, not 30.</li>
      </UL>
      <H2>4. Freelancers — $1–3/month</H2>
      <UL>
        <li>Hire on Upwork / Reddit r/coldemail; they bulk-create using their own CSP access.</li>
        <li>Cheapest, riskiest. Vet the seller — bad sellers ship pre-flagged tenants.</li>
        <li>Spread tenants across multiple sellers so one bad batch doesn't take you offline.</li>
      </UL>
      <Callout kind="tip">
        The economics flip past ~30 tenants: CSP/partner becomes
        cheapest by a wide margin. Until then, "buy from us" is the
        lowest-friction option.
      </Callout>
    </>
  ),

  'microsoft-tenants/pre-flight': () => (
    <>
      <P>Before adding a tenant to Sendepot, confirm these. Skipping them costs hours.</P>
      <H2>Tenant</H2>
      <UL>
        <li>Admin login is reachable — you can sign in to <Ext href="https://admin.microsoft.com">admin.microsoft.com</Ext> with the credentials you have.</li>
        <li>The admin user has at least one paid M365 license attached (otherwise mailbox creation fails silently).</li>
        <li>MFA / Conditional Access is OFF on the admin user. Sendepot logs in via Playwright; MFA prompts will hang the run.</li>
      </UL>
      <H2>Domain</H2>
      <UL>
        <li>You own the domain at a registrar you can edit DNS on (Namecheap, Porkbun, Cloudflare, GoDaddy — any).</li>
        <li>The domain isn't already attached to a different M365 tenant.</li>
        <li>Nothing important is currently hosted on it. Sendepot will manage MX, SPF, DKIM, DMARC, and Autodiscover for the domain.</li>
      </UL>
      <H2>Sequencer</H2>
      <UL>
        <li>You've added an Instantly or Smartlead key in <AppLink to="/settings">Settings → Sequencer API keys</AppLink>.</li>
        <li>For Smartlead, you've also pasted the OAuth URL — see <AppLink to="/docs/sequencers/smartlead">Connecting Smartlead</AppLink>.</li>
      </UL>
    </>
  ),

  'microsoft-tenants/become-a-partner': () => (
    <>
      <P>
        For high-volume operators (50+ tenants/month), becoming a
        Microsoft CSP partner gets you per-tenant cost down to ~$4.75
        and creation time to ~15 minutes. Trade-off: 1–4 week approval
        and significant verification paperwork.
      </P>
      <H2>The fast path: Pax8</H2>
      <P>
        Pax8 is a Microsoft-approved indirect CSP that handles much of
        the partner enrollment for you. Sign up at{' '}
        <Ext href="https://www.pax8.com">pax8.com</Ext>, complete their
        onboarding, and you're issued partner access without going
        through the full Microsoft Partner Network application yourself.
      </P>
      <H2>The hard path: direct MPN</H2>
      <OL>
        <li>Create a Microsoft Partner account.</li>
        <li>Identity verification — government ID + a verifiable phone number.</li>
        <li>Domain ownership proof (TXT record on a domain you control).</li>
        <li>Employment verification — Microsoft confirms with your employer.</li>
        <li>Business verification — registered business + verifiable corporate website.</li>
        <li>First submission almost always rejects. Reply via support ticket and it goes to manual review.</li>
      </OL>
      <Callout kind="warn">
        Don't read like a cold-email shop on your business website.
        Microsoft auto-flags applications that mention "cold email,"
        "outbound," or "lead generation" in the wrong way. Frame your
        business as B2B services or consulting.
      </Callout>
      <H2>What you get when approved</H2>
      <UL>
        <li>Direct CSP pricing on M365 licenses.</li>
        <li>Bulk tenant creation via the partner portal.</li>
        <li>The ability to onboard tenants for clients without them seeing your costs.</li>
      </UL>
    </>
  ),

  // ── Sequencers ──────────────────────────────────────────────────────
  'sequencers/overview': () => (
    <>
      <P>
        A "sequencer" is the cold-email platform that actually sends
        your campaigns. Sendepot delivers ready-to-send mailboxes into
        the sequencer of your choice via OAuth.
      </P>
      <H2>Live</H2>
      <UL>
        <li><AppLink to="/docs/sequencers/instantly">Instantly</AppLink> — broadly recommended for Outlook.</li>
        <li><AppLink to="/docs/sequencers/smartlead">Smartlead</AppLink> — works, but read the warnings before using.</li>
      </UL>
      <H2>Coming</H2>
      <UL>
        <li>PlusVibe — fastest to land for Outlook in our testing.</li>
        <li>EmailBison — adopted by power users; we'll add native upload soon.</li>
      </UL>
      <Callout kind="warn">
        See <AppLink to="/docs/sequencers/smartlead">the Smartlead page</AppLink>{' '}
        before picking it: Smartlead has a known issue with Outlook
        bursting that can kill tenants.
      </Callout>
      <H2>How keys work</H2>
      <P>
        Each workspace can hold many keys per sequencer (e.g. several
        Smartlead workspaces). Tenants pin to a specific key via
        <Code>tenant.sequencer_key_id</Code>. Omit it to use the workspace
        default. Manage keys in <AppLink to="/settings">Settings → Sequencer API keys</AppLink>.
      </P>
    </>
  ),

  'sequencers/instantly': () => (
    <>
      <P>Instantly is the smoothest integration. Two minutes total.</P>
      <OL>
        <li>Sign in to <Ext href="https://app.instantly.ai">app.instantly.ai</Ext> → Settings → Integrations → API keys.</li>
        <li>Create a new key. Scopes: at minimum <strong>email accounts: read + write</strong>.</li>
        <li>Copy the key into <AppLink to="/settings">Sendepot → Settings → Sequencer API keys → Add key (Instantly)</AppLink>.</li>
        <li>Label it (e.g. "main", "agency-X") if you'll have more than one.</li>
      </OL>
      <P>
        That's it. New tenants pinned to <Code>instantly</Code> will OAuth
        every mailbox into this account.
      </P>
      <H2>Multiple Instantly accounts</H2>
      <P>
        See <AppLink to="/docs/sequencers/multi-account">Multiple accounts on one sequencer</AppLink>{' '}
        — every key has a label and tenants can pin individually.
      </P>
    </>
  ),

  'sequencers/smartlead': () => (
    <>
      <Callout kind="warn">
        Smartlead has a documented issue with Outlook bursting — it
        sometimes sends 4–5x the configured warm-up volume, which
        blocks tenants. If you're choosing now, try Instantly first.
        See <AppLink to="/docs/best-practices/warmup">warm-up schedules</AppLink>{' '}
        for the conservative settings if you must use it.
      </Callout>
      <H2>Why Smartlead needs an OAuth URL</H2>
      <P>
        Smartlead routes new mailbox connections back to a workspace-specific
        OAuth callback. Their API key alone doesn't tell us which
        workspace to attach mailboxes to — we need the workspace's
        <Code>user_id</Code>, which lives in their OAuth URL's <Code>state</Code> parameter.
      </P>
      <P>Sendepot only reads the <Code>user_id</Code> integer; no credentials are extracted.</P>
      <H2>Getting the OAuth URL</H2>
      <OL>
        <li>Sign in to <Ext href="https://app.smartlead.ai">app.smartlead.ai</Ext>.</li>
        <li>Sidebar → <strong>Email Accounts</strong> → <strong>Add Email Accounts</strong>.</li>
        <li>Choose <strong>Microsoft / Office 365</strong> (sometimes labelled "Outlook").</li>
        <li>
          A Microsoft sign-in tab opens. <strong>Don't sign in.</strong>{' '}
          Just look at the URL — it starts with{' '}
          <Code>https://login.microsoftonline.com/…</Code> and contains{' '}
          <Code>state=&#123;"user_id":NNNN&#125;</Code>.
        </li>
        <li>Copy the entire URL. Close the Microsoft tab without signing in.</li>
      </OL>
      <H2>Adding the key</H2>
      <P>
        In <AppLink to="/settings">Sendepot → Settings → Sequencer API keys</AppLink>, click <strong>Add key (Smartlead)</strong>:
      </P>
      <UL>
        <li><strong>API key</strong> — from Smartlead → Settings → API.</li>
        <li><strong>OAuth URL</strong> — paste the full URL you grabbed above.</li>
        <li><strong>Label</strong> — distinguishing name if you have multiple Smartlead workspaces.</li>
      </UL>
      <P>You only do this once per Smartlead workspace.</P>
    </>
  ),

  'sequencers/multi-account': () => (
    <>
      <P>
        A single Sendepot workspace can hold many keys per sequencer.
        Each key has a label; each tenant pins to one. This is how
        agencies route different clients to different Instantly /
        Smartlead workspaces from the same Sendepot account.
      </P>
      <H2>Managing keys</H2>
      <UL>
        <li>Add: <AppLink to="/settings">Settings → Sequencer API keys → Add key</AppLink>. Each gets a label.</li>
        <li>Default: one key per sequencer is the default. New tenants without an explicit pin use it.</li>
        <li>Pin a tenant: select the labelled key from the sequencer dropdown when creating the tenant.</li>
      </UL>
      <H2>From the API</H2>
      <Pre>{`POST /api/clients/<workspace>/sequencer-keys
{
  "sequencer": "instantly",
  "api_key":   "ins_…",
  "label":     "agency-X",
  "is_default": false
}`}</Pre>
      <P>Then on tenant creation, pass <Code>sequencer_key_label: "agency-X"</Code>.</P>
    </>
  ),

  // ── Provisioning ────────────────────────────────────────────────────
  'provisioning/first-tenant': () => (
    <>
      <P>From <AppLink to="/tenants">Tenants</AppLink>, click <strong>+ New tenant</strong>. The form asks for:</P>
      <UL>
        <li><strong>Tenant login</strong> — admin email, e.g. <Code>admin@mycorp.onmicrosoft.com</Code>.</li>
        <li><strong>Initial password</strong> — current admin password. Sendepot rotates it within minutes.</li>
        <li><strong>Sending domain</strong> — domain you want mailboxes on.</li>
        <li><strong>Sequencer</strong> — Instantly or Smartlead.</li>
        <li><strong>Sequencer key</strong> — only if you have multiple keys; defaults to the workspace default.</li>
        <li><strong>Mailbox names</strong> — one or more comma-separated seed names (e.g. <Code>Gavin Belson, Jane Doe, John Smith</Code>). Sendepot generates permutations of each (jane.doe@, j.doe@, jane.d@, …) and splits the requested mailbox count evenly across them. Leave blank for fully random names.</li>
        <li><strong>Mailbox count</strong> — how many mailboxes to provision on this tenant. Min 25, max 150, default 100.</li>
      </UL>
      <P>Submit. Sendepot starts provisioning immediately and you're redirected to the tenant detail view with a live progress bar.</P>
      <H2>What happens next</H2>
      <P>Provisioning runs end-to-end without your involvement. Same-day delivery — typically a few hours from submit to every mailbox connected (25–150 per tenant, your choice; default 100), most of that DNS propagation. See <AppLink to="/docs/provisioning/phases">What runs behind the scenes</AppLink> for the high-level stages.</P>
    </>
  ),

  'provisioning/phases': () => (
    <>
      <P>
        Once you submit a tenant, Sendepot runs through a sequence of
        provisioning stages. You don't trigger any of them — they kick
        off automatically and run end-to-end. The progress bar in the
        tenant detail view climbs as each stage completes.
      </P>
      <H2>The four stages</H2>
      <OL>
        <li><strong>Tenant connected</strong> — admin access verified.</li>
        <li><strong>Domain configured</strong> — your sending domain is attached to the tenant and ready to send from.</li>
        <li><strong>Mailboxes created</strong> — the count you picked (25–150) of sender addresses on your domain, generated from the names you supplied.</li>
        <li><strong>Sequencer connected</strong> — every mailbox is connected to your Instantly or Smartlead account.</li>
      </OL>
      <H2>Self-healing</H2>
      <P>
        Transient failures (network blips, Microsoft rate-limits, DNS
        propagation delays) retry automatically up to a few times with
        backoff. You only need to look at a tenant if it ends up in the{' '}
        <Code>failed</Code> state after retries are exhausted.
      </P>
      <H2>If something does fail</H2>
      <UL>
        <li>The tenant detail view shows the failing stage and the underlying error.</li>
        <li>Most failures are transient — restart from the tenant detail view if needed.</li>
        <li>For persistent failures (e.g. wrong admin password, missing license), check <AppLink to="/docs/troubleshooting/aadsts-errors">common errors</AppLink>.</li>
      </UL>
    </>
  ),

  'provisioning/bulk': () => (
    <>
      <P>Adding many tenants is the same flow run in parallel.</P>
      <H2>From the UI</H2>
      <P>
        On the New tenant form, switch to <strong>Bulk</strong> mode and
        paste a CSV with one tenant per row:
      </P>
      <Pre>{`admin_email,password,domain,sequencer,name_seed
admin@a.onmicrosoft.com,Pa55w0rd!,a-domain.com,instantly,Jane Doe
admin@b.onmicrosoft.com,Pa55w0rd!,b-domain.com,instantly,John Smith
...`}</Pre>
      <H2>Recommended pacing</H2>
      <UL>
        <li>Submit in batches of 10. Wait for them to finish, then send the next 10.</li>
        <li>Microsoft's admin console rate-limits per IP — going past 10 concurrent runs increases failure rate.</li>
        <li>Each tenant uses a different proxy if your workspace has proxies configured; otherwise it shares.</li>
      </UL>
      <H2>From the API</H2>
      <P>
        <Code>POST /api/tenants/bulk</Code> with{' '}
        <Code>&#123;rows: [...]&#125;</Code>. Same shape as creating one
        but accepts an array. See the <Ext href="https://sendepot.com/api/docs">Swagger reference</Ext>.
      </P>
    </>
  ),

  'provisioning/progress': () => (
    <>
      <P>From <AppLink to="/tenants">Tenants</AppLink>, click any tenant. The detail view shows:</P>
      <UL>
        <li><strong>Progress bar</strong> — overall completion percentage across the seven phases.</li>
        <li><strong>Per-phase status</strong> — pending / running / done / failed.</li>
        <li><strong>Mailboxes tab</strong> — every mailbox + its individual OAuth status.</li>
        <li><strong>Logs tab</strong> — admin-only; raw output from Playwright + Graph calls.</li>
      </UL>
      <H2>Status colors</H2>
      <UL>
        <li><span className="text-run">Running</span> — work is in flight.</li>
        <li><span className="text-warn">Warning</span> — completed but with mailbox-level errors. Reconnect failed mailboxes individually.</li>
        <li><span className="text-err">Failed</span> — a phase errored. Re-run from the detail view.</li>
        <li><span className="text-ok">Done</span> — every mailbox is in the sequencer.</li>
      </UL>
      <H2>From the API</H2>
      <Pre>{`# Poll a single tenant
curl https://sendepot.com/api/tenants/<id> \\
  -H "Authorization: Bearer ie_..."

# Counts across all your tenants
curl https://sendepot.com/api/tenants \\
  -H "Authorization: Bearer ie_..." | \\
  jq 'group_by(.status) | map({status: .[0].status, n: length})'`}</Pre>
    </>
  ),

  // ── Best practices ──────────────────────────────────────────────────
  'best-practices/reply-rates': () => (
    <>
      <P>
        Reply rates have declined every year since 2020. The platform is
        not the lever — your execution is. Sendepot can hand you 1,000
        ready mailboxes; what they return is up to your copy, list, and
        warm-up discipline.
      </P>
      <H2>Realistic expectations</H2>
      <UL>
        <li>1–6% reply rate is the realistic range for cold campaigns in 2026.</li>
        <li>Without personalization, expect the lower end (1–2%).</li>
        <li>With heavy personalization + 2-step "conversation starter" sequences, 4–6% is achievable.</li>
        <li>Anyone promising "10% reply rates" is selling you something.</li>
      </UL>
      <H2>What actually moves the needle</H2>
      <OL>
        <li><strong>Spintax + 3+ copy variants per campaign.</strong> ESPs run AI on inbound to detect duplicates. <AppLink to="/docs/best-practices/spintax">Details →</AppLink></li>
        <li><strong>Domain rotation every 2–4 weeks.</strong> Domains burn; refresh them before they're poisoned. <AppLink to="/docs/best-practices/domain-rotation">Details →</AppLink></li>
        <li><strong>Conservative warm-up + send caps.</strong> Never exceed 5 cold/day per mailbox. <AppLink to="/docs/best-practices/warmup">Details →</AppLink></li>
        <li><strong>Segment Outlook and Google audiences.</strong> Different copy works for each. <AppLink to="/docs/best-practices/outlook-vs-google">Details →</AppLink></li>
        <li><strong>Speed-to-lead under 5 min.</strong> Reinvest the savings on infra into a fast first-response system.</li>
      </OL>
      <H2>What does NOT move the needle</H2>
      <UL>
        <li>Buying tenants from a fancier reseller — same Microsoft infrastructure.</li>
        <li>Running mass inbox-placement tests — actively harms reputation. <AppLink to="/docs/best-practices/avoid-mass-tests">Why →</AppLink></li>
        <li>Switching from Sendepot to another tool. The tool isn't the bottleneck.</li>
      </UL>
    </>
  ),

  'best-practices/outlook-vs-google': () => (
    <>
      <P>
        These are different platforms with different economics. Picking
        the wrong one for your audience costs 2–5x.
      </P>
      <H2>Cost comparison (25k emails/day)</H2>
      <Pre>{`Google      ~$3,100/month   (frequent domain rotation expensive)
Outlook     ~$900/month     (multi-domain approach)
Savings:    ~$2,200/month`}</Pre>
      <H2>Reply-rate comparison</H2>
      <UL>
        <li>Google: ~10% higher reply rates on average.</li>
        <li>Outlook: more variable per-tenant; quality of CSP matters a lot.</li>
        <li>The right metric is <strong>cost-per-reply</strong>, not raw reply rate.</li>
      </UL>
      <H2>Use Google when</H2>
      <UL>
        <li>Your TAM is small (5k accounts or fewer).</li>
        <li>Reply rate matters more than budget efficiency.</li>
        <li>Audience is predominantly Google Workspace (SMBs, modern startups).</li>
      </UL>
      <H2>Use Outlook when</H2>
      <UL>
        <li>Volume needs are higher (10k+/day).</li>
        <li>Audience is enterprise / financial services / regulated industries (80–90% Outlook).</li>
        <li>Cost-per-reply is the success metric.</li>
      </UL>
      <Callout kind="warn">
        Don't mix in one campaign. ESP-matched copy that wins on Gmail
        will tank on Outlook (and vice-versa). Segment campaigns by
        recipient ESP and run separate copy for each.
      </Callout>
    </>
  ),

  'best-practices/warmup': () => (
    <>
      <P>The single most-broken setting on every cold-email account. Get this wrong and tenants die in week one.</P>
      <H2>Three working schedules</H2>
      <H3>Option 1 — Ramp (recommended)</H3>
      <UL>
        <li>Days 1–5: warm-up emails ramp 1 → 2 → 3 → 4 → 5 per day.</li>
        <li>Days 6–12: hold at 5/day.</li>
        <li>Cold emails: start day 5, max 3/day. Day 12+ go to 5/day cold.</li>
      </UL>
      <H3>Option 2 — Conservative flat</H3>
      <UL>
        <li>5 warm-up emails/day for 14 days, no ramp.</li>
        <li>Cold emails: start day 8, capped at 5/day.</li>
      </UL>
      <H3>Option 3 — Simple</H3>
      <UL>
        <li>5 warm-up/day for 7–14 days.</li>
        <li>Then start cold. Same 5/day ceiling.</li>
      </UL>
      <H2>Universal rules</H2>
      <UL>
        <li><strong>Never exceed 5 cold emails/day per mailbox.</strong> 3 is safer.</li>
        <li>Warm-up keeps running alongside cold. Don't stop it.</li>
        <li>Disable "auto-adjust ramp-up" — it ignores your cap and overshoots.</li>
        <li>Smartlead users: also disable "Enable Auto-Adjust Warmup/Sending Ratio" or it can send 100 warm-ups/day.</li>
      </UL>
      <H2>The aging tactic (advanced)</H2>
      <P>
        Some operators leave a tenant fully provisioned but idle for ~7
        days before starting warm-up. Theory: Microsoft favors tenants
        that look "lived in" before they start emailing. Most don't
        bother — the data isn't conclusive — but it's free.
      </P>
    </>
  ),

  'best-practices/spintax': () => (
    <>
      <P>
        ESPs now run cheap LLMs against inbound mail to detect copy
        repetition across senders. Without copy variation, you'll be
        in spam by week two.
      </P>
      <H2>Minimum viable variation</H2>
      <UL>
        <li><strong>3–4 distinct copy versions per campaign.</strong> Not just spintax — actually different angles.</li>
        <li>Different message lengths (short ~50 words, medium ~120 words).</li>
        <li>Different CTA framings (question, soft ask, calendar link).</li>
        <li>Different value-prop angles (cost, outcome, social proof).</li>
        <li>Spintax inside each version for vocabulary variation.</li>
      </UL>
      <H2>Spintax basics</H2>
      <Pre>{`Hey {Hi|Hey there},

I noticed {your team|the folks at {{company}}} are
{growing|expanding|scaling} fast — congrats on the {recent funding|new role|launch}.`}</Pre>
      <P>
        Most sequencers parse this format. Aim for 4–6 spinpoints per
        sentence; avoid spinning so heavily that sentences become
        nonsense.
      </P>
      <Callout kind="tip">
        If your variants all hit the same value prop, it's not real
        variation — ESPs cluster on semantic meaning, not just words.
        Hand a junior copywriter 3 hours and have them write 4 angles
        from scratch.
      </Callout>
    </>
  ),

  'best-practices/domain-rotation': () => (
    <>
      <P>
        Domains are consumables. Plan to retire them every 2–4 weeks of
        active sending — earlier if reply rates dip.
      </P>
      <H2>The economics</H2>
      <Pre>{`50 domains, 25k emails/day:
  ~$150 for full rotation       (.info / .co / .net registrar fee)
  → instant fresh deliverability

Equivalent on Google:
  $1,000+ and harder to manage`}</Pre>
      <H2>When to rotate a domain</H2>
      <UL>
        <li>Reply rate drops 30–50% from baseline (track per-domain daily).</li>
        <li>Bounce rate suddenly spikes.</li>
        <li>Warm-up score declines week-over-week.</li>
        <li>2–4 weeks of active sending, regardless of metrics.</li>
      </UL>
      <H2>Operational tips</H2>
      <UL>
        <li>Keep ~20% spare domains warmed and ready.</li>
        <li>Use Sendepot's <AppLink to="/docs/domain-swap/overview">Domain swap</AppLink> to move every mailbox on a tenant to a new domain in one click.</li>
        <li>Don't be cheap. <Code>.info</Code> domains cost ~$3 — saving on domains while losing replies is bad math.</li>
      </UL>
    </>
  ),

  'best-practices/sending-to-outlook': () => (
    <>
      <P>
        Outlook → Outlook is the hardest combination in cold email.
        Microsoft applies pattern recognition, AI content analysis, and
        semantic-similarity detection across all inbound. Stuff that
        works at 2% reply rate against Gmail can hit 0.3% against
        Outlook recipients without changes.
      </P>
      <H2>Three strategies that work</H2>
      <H3>1. Two-step conversation</H3>
      <P>
        First email is dead-simple, ~3 lines, zero pitch. Just a
        relevance hook + a question. Pitch only happens after they
        reply. Triples response rates against Outlook recipients in
        practice — Microsoft is pattern-matching for sales language,
        and your first email doesn't have any.
      </P>
      <H3>2. Real personalization</H3>
      <P>
        Reference something specific: a recent funding round, a new
        hire, a comment they made on a podcast. Generic <Code>&#123;&#123;company&#125;&#125;</Code>{' '}
        merge fields are easy for Microsoft to detect.
      </P>
      <H3>3. Volume rotation</H3>
      <P>
        Run 8+ copy variants simultaneously, with heavy spintax inside
        each. Rotate domains weekly. This is the brute-force approach —
        works but is expensive.
      </P>
      <H2>Industry mix matters</H2>
      <UL>
        <li><strong>Financial services</strong>: 80–90% Outlook. Very strict filtering. Two-step or extreme personalization only.</li>
        <li><strong>Enterprise</strong>: Barracuda / Mimecast / Proofpoint sit in front of Outlook. Standard tactics often fail entirely.</li>
        <li><strong>SMB</strong>: 60–70% Google, 20–30% Outlook. Easier ground.</li>
      </UL>
    </>
  ),

  'best-practices/avoid-mass-tests': () => (
    <>
      <Callout kind="warn">
        Mass inbox-placement testing tools (GlockApps, Mailgenius mass
        runs, etc.) test all your accounts at once. This is the
        single fastest way to get a tenant blocked.
      </Callout>
      <H2>Why it kills tenants</H2>
      <P>
        Sending 100 emails to seed accounts in a 5-minute window looks
        identical to a spam burst from Microsoft's monitoring side. The
        velocity triggers a tenant-level block (550 5.7.705) that you
        cannot recover from. <AppLink to="/docs/tenant-blocking/overview">Tenant blocking →</AppLink>
      </P>
      <H2>Test correctly</H2>
      <OL>
        <li>Test 3–4 mailboxes per domain, not all 100.</li>
        <li>Spread tests across multiple days, not the same hour.</li>
        <li>Domain and IP reputation drive deliverability — individual mailbox testing tells you almost nothing more than a 4-mailbox sample does.</li>
      </OL>
      <H2>Better signal than seed-list testing</H2>
      <UL>
        <li>Reply rate trends per domain (drop 3% → 1% means spam folder).</li>
        <li>Warm-up reputation scores in your sequencer's dashboard.</li>
        <li>Bounce-rate trends.</li>
      </UL>
    </>
  ),

  // ── Tenant blocking ─────────────────────────────────────────────────
  'tenant-blocking/overview': () => (
    <>
      <Callout kind="warn">
        Tenant-level blocking is unrecoverable. Microsoft will not
        unlock the tenant, will not respond to support requests, will
        not explain. Your best play is prevention.
      </Callout>
      <H2>What it looks like</H2>
      <P>
        Every mailbox on a tenant starts bouncing with{' '}
        <Code>550 5.7.705 Threshold Exceeded</Code>. Sending stops
        instantly across the entire tenant. There is no recovery
        pathway.
      </P>
      <H2>What triggers it</H2>
      <UL>
        <li>Velocity — too many emails in too short a window.</li>
        <li>Burst sending — sequencer ramps faster than configured (Smartlead is notorious for this).</li>
        <li>The first 24 hours after provisioning are highest risk: even modest sending can trigger.</li>
        <li>Random — Microsoft's models flag ~5% of new tenants regardless of behavior.</li>
      </UL>
      <H2>Prevention</H2>
      <OL>
        <li><strong>24-hour rest after provisioning.</strong> No sending, no warm-up. Just leave it.</li>
        <li><strong>Conservative warm-up only.</strong> Max 5/day. <AppLink to="/docs/best-practices/warmup">Schedules →</AppLink></li>
        <li><strong>Disable burst-prevention overrides.</strong> The "auto-adjust" / "burst smoothing" features lie about respecting your caps.</li>
        <li><strong>Backup tenants.</strong> Random blocks happen. Always have a 20% over-provisioned buffer.</li>
        <li><strong>Don't mass-test.</strong> See <AppLink to="/docs/best-practices/avoid-mass-tests">Don't mass-test</AppLink>.</li>
      </OL>
    </>
  ),

  'tenant-blocking/recovery': () => (
    <>
      <P>There is no recovery once a tenant is blocked at the platform level. You can confirm the diagnosis, then move on.</P>
      <H2>Confirm the block</H2>
      <UL>
        <li>Open the tenant in <AppLink to="/tenants">Tenants</AppLink> → status flips to <Code>warning</Code> or <Code>failed</Code>.</li>
        <li>Mailboxes tab — every mailbox shows <Code>550 5.7.705</Code> in the error column.</li>
        <li>If only some mailboxes fail, it's NOT a tenant block — see <AppLink to="/docs/troubleshooting/inbox-disconnects">inbox disconnects</AppLink>.</li>
      </UL>
      <H2>What to do</H2>
      <OL>
        <li><strong>Stop all sending on that tenant.</strong> Your sequencer is wasting budget firing into the void.</li>
        <li><strong>Tear down the tenant in Sendepot.</strong> From the detail view → <strong>Teardown</strong>. This deletes the mailboxes and frees the licenses.</li>
        <li><strong>Move the domain to a fresh tenant.</strong> The domain itself is fine; it's the tenant Microsoft killed. Use <AppLink to="/docs/domain-swap/overview">Domain swap</AppLink>.</li>
        <li><strong>Investigate root cause.</strong> Almost always burst sending. Check sequencer logs for the days before the block.</li>
      </OL>
      <Callout>
        Don't waste time contacting Microsoft support. Even partner-status
        tickets on this go unanswered. Burnt tenant = burnt tenant.
      </Callout>
    </>
  ),

  // ── Mailboxes ───────────────────────────────────────────────────────
  'mailboxes/list': () => (
    <>
      <P>From any tenant detail view → <strong>Mailboxes</strong> tab. Each row shows:</P>
      <UL>
        <li>Email address (after UPN flip — <Code>name@yourdomain.com</Code>).</li>
        <li>Status: <Code>connected</Code> / <Code>provisioning</Code> / <Code>failed</Code>.</li>
        <li>Error message if any.</li>
        <li>Per-step booleans (admin only) — created, licensed, password set, UPN flipped, OAuth done.</li>
      </UL>
      <H2>From the API</H2>
      <Pre>{`GET /api/tenants/<id>/mailboxes
  → list. Non-admins see email + status + error_message only.

GET /api/tenants/<id>/mailboxes/<email>/password
  → admin / cookie session only. Decrypts and returns the password.`}</Pre>
    </>
  ),

  'mailboxes/reconnect': () => (
    <>
      <P>
        OAuth tokens drop occasionally — Microsoft invalidates them, the
        sequencer pauses an account, the user clicks "deny" on a re-auth.
        Reconnecting is a one-click fix per mailbox.
      </P>
      <H2>From the UI</H2>
      <OL>
        <li>Open the tenant → <strong>Mailboxes</strong>.</li>
        <li>Click <strong>Reconnect</strong> next to any mailbox showing <Code>failed</Code>.</li>
        <li>Sendepot reissues the OAuth handshake without touching other mailboxes.</li>
      </OL>
      <H2>From the API</H2>
      <Pre>{`POST /api/tenants/<id>/reconnect
  body: { "emails": ["jane@yourdomain.com", ...] }`}</Pre>
      <P>Or pass <Code>"all": true</Code> to retry every failed mailbox.</P>
    </>
  ),

  'mailboxes/reset-password': () => (
    <>
      <P>
        Sometimes you need to reset a mailbox password — usually because
        the sequencer flagged it for re-authentication and the OAuth
        path isn't working.
      </P>
      <H2>From the UI</H2>
      <OL>
        <li>Tenant → <strong>Mailboxes</strong> → click the mailbox row.</li>
        <li><strong>Reset password</strong> action. Sendepot generates a new strong password, updates Microsoft, and re-OAuths the sequencer.</li>
      </OL>
      <H2>Bulk reset</H2>
      <P>
        From the tenant detail view → <strong>Reset all passwords</strong>.
        Useful after a tenant block to nullify the old credentials before
        teardown.
      </P>
    </>
  ),

  'mailboxes/export-csv': () => (
    <>
      <P>
        Need full credentials for a sequencer that doesn't OAuth? Export
        a CSV with each mailbox's full name, email, and password.
      </P>
      <H2>One tenant</H2>
      <P>
        Tenant detail → <strong>Mailboxes</strong> → <strong>Export CSV</strong>.
        Admin export includes passwords + per-step state.
      </P>
      <H2>Many tenants combined</H2>
      <OL>
        <li>From <AppLink to="/tenants">Tenants</AppLink>, tick the checkbox on each tenant you want.</li>
        <li>Click <strong>Download CSV (N)</strong> in the header.</li>
        <li>You get a single combined CSV: <Code>full_name,email,password</Code>.</li>
      </OL>
      <Callout kind="warn">
        That CSV contains plaintext passwords. Treat it like any other
        credential dump — don't email it, don't paste into Slack
        channels with broad membership.
      </Callout>
    </>
  ),

  // ── Domain swap ─────────────────────────────────────────────────────
  'domain-swap/overview': () => (
    <>
      <P>
        Domain swap rewrites every mailbox on a tenant from{' '}
        <Code>name@old-domain.com</Code> to <Code>name@new-domain.com</Code>{' '}
        in one operation. Sendepot adds the new domain to the tenant,
        configures DNS, flips each mailbox's primary address, and
        cleans up the old domain.
      </P>
      <H2>When to use it</H2>
      <UL>
        <li>Old domain's reputation is degraded (reply rates dropped 50%+).</li>
        <li>You're proactively rotating per <AppLink to="/docs/best-practices/domain-rotation">domain rotation strategy</AppLink>.</li>
        <li>The domain itself was burned but the tenant is healthy.</li>
      </UL>
      <H2>How it works</H2>
      <OL>
        <li>From the tenant detail view → <strong>Domain swap</strong> tab.</li>
        <li>Enter the new domain. Sendepot adds it to the tenant + verifies + sets DNS.</li>
        <li>Each mailbox gets a new alias on the new domain, flips its primary, drops the old alias.</li>
        <li>Sequencer OAuth is re-issued so the sequencer sees the new addresses.</li>
        <li>Old domain is removed from the tenant.</li>
      </OL>
      <P>Same-day end-to-end. Mailboxes are briefly unavailable during the cutover.</P>
      <H2>If it gets stuck</H2>
      <P>
        Most stuck swaps are domain-verification timeouts (Microsoft
        being slow). The pipeline retries automatically; for persistent
        sticks, contact <Ext href="mailto:support@sendepot.com">support</Ext>.
      </P>
    </>
  ),

  // ── Troubleshooting ─────────────────────────────────────────────────
  'troubleshooting/inbox-disconnects': () => (
    <>
      <P>Two scenarios — the fix depends on which.</P>
      <H2>Scenario A: a few mailboxes disconnected</H2>
      <P>OAuth token expired or sequencer paused the account. Fix per-mailbox:</P>
      <OL>
        <li>Tenant → <strong>Mailboxes</strong> → click <strong>Reconnect</strong> on each affected row.</li>
        <li>If reconnect repeatedly fails, reset the password (<AppLink to="/docs/mailboxes/reset-password">how →</AppLink>) and try again.</li>
      </OL>
      <H2>Scenario B: ALL mailboxes on a tenant disconnected</H2>
      <P>This is usually one of:</P>
      <UL>
        <li><strong>Tenant blocked</strong> — see <AppLink to="/docs/tenant-blocking/recovery">tenant blocking</AppLink>. Mailboxes show <Code>550 5.7.705</Code>.</li>
        <li><strong>DNS broke</strong> — someone changed nameservers at the registrar. Check the domain's NS records via <Ext href="https://nslookup.io">nslookup.io</Ext>.</li>
        <li><strong>DKIM keys cycled</strong> — verify selectors via <Ext href="https://easydmarc.com/tools/dkim-lookup">EasyDMARC's DKIM checker</Ext>.</li>
      </UL>
      <Callout kind="tip">
        Sendepot sets DNS automatically at provision time. If something
        broke later, it was changed externally. Check who has access to
        the registrar.
      </Callout>
    </>
  ),

  'troubleshooting/stuck-tenants': () => (
    <>
      <P>
        A tenant marked <Code>running</Code> for &gt;90 minutes is
        usually stuck on DNS propagation or a flaky Microsoft API. The
        worker retries automatically up to 4 times before flagging
        failure.
      </P>
      <H2>Self-diagnosis</H2>
      <OL>
        <li>Tenant detail → <strong>Logs</strong> tab (admin) — look for the most recent timestamp. If it was &gt;30 min ago, the worker is wedged.</li>
        <li>Check phase-by-phase. If <strong>Domain verification</strong> is stuck, Microsoft is being slow on TXT polling. Wait or re-run.</li>
        <li>If <strong>Mailbox creation</strong> is stuck, your tenant may not have enough licenses for the requested mailbox count. Buy more in M365 admin, or recreate the tenant with a smaller mailbox count.</li>
      </OL>
      <H2>Force re-run</H2>
      <P>From the tenant detail view → <strong>Re-run pipeline</strong>. Restarts from the failed phase forward.</P>
      <H2>From the API</H2>
      <Pre>{`POST /api/tenants/<id>/pipeline/run
  body: { "phase": "mailbox_creation" }   # or omit to start from beginning`}</Pre>
    </>
  ),

  'troubleshooting/aadsts-errors': () => (
    <>
      <P>Microsoft's auth errors show up as <Code>AADSTS&lt;number&gt;</Code> codes. The common ones we see:</P>
      <H3>AADSTS50126</H3>
      <P>Wrong username or password. Most often: stored password is stale (someone changed it manually in M365). Fix: update the tenant's admin password in Sendepot's tenant detail view.</P>
      <H3>AADSTS65004</H3>
      <P>"User declined to consent." Sendepot's Playwright bot clicked the wrong button on a consent page. Re-run the OAuth phase. If persistent, contact support.</P>
      <H3>AADSTS50053</H3>
      <P>Account locked due to too many sign-in attempts. Wait 15 minutes, then re-run. To prevent: don't run the pipeline more than 3x in quick succession.</P>
      <H3>AADSTS50076 / AADSTS50079</H3>
      <P>MFA required. Sendepot can't drive MFA. Disable MFA on the admin user, or carve out a Conditional Access exception.</P>
      <H3>AADSTS500011 / AADSTS650056</H3>
      <P>Service principal doesn't exist or admin consent missing. Re-run the admin-consent phase from the tenant detail view.</P>
      <H3>AADSTS901001</H3>
      <P>Unsupported prompt parameter. Internal — affects older Sendepot deploys. Update should resolve. Contact support.</P>
      <H2>Anything else</H2>
      <P>
        Search the AADSTS code at{' '}
        <Ext href="https://login.microsoftonline.com/error">login.microsoftonline.com/error</Ext> —
        Microsoft's error explorer often has the answer. If it's a code
        we should be handling but aren't,{' '}
        <Ext href="mailto:support@sendepot.com">tell us</Ext>.
      </P>
    </>
  ),

  // ── Account & billing ───────────────────────────────────────────────
  'account/sign-in': () => (
    <>
      <P>
        Sendepot uses magic-link sign-in — no password to remember.
        Enter your email, click the link in your inbox.
      </P>
      <H2>How it works</H2>
      <OL>
        <li>Enter your email at <Ext href="https://sendepot.com/login">sendepot.com/login</Ext>.</li>
        <li>We email a single-use link valid for 15 minutes.</li>
        <li>Click → you're in. Session lasts 30 days.</li>
      </OL>
      <H2>Didn't get the link?</H2>
      <UL>
        <li>Check spam / promotions. Sender is <Code>hello@sendepot.com</Code>.</li>
        <li>Wait 60 seconds before re-requesting (rate limit).</li>
        <li>If still nothing after 5 minutes, contact <Ext href="mailto:support@sendepot.com">support</Ext> — admins can resend.</li>
      </UL>
      <H2>Security model</H2>
      <UL>
        <li>Tokens are sha256-hashed at rest — a DB leak doesn't grant access.</li>
        <li>Single-use: clicking the link burns it. The next sign-in needs a fresh link.</li>
        <li>15-minute expiry.</li>
        <li>The email body itself never contains login credentials, just the one-time link.</li>
      </UL>
      <H2>What about Google / Microsoft OAuth?</H2>
      <P>Coming soon. For now, magic links are the only end-user auth path.</P>
    </>
  ),

  'account/workspace': () => (
    <>
      <P>
        Each Sendepot account is one workspace. Tenants, sequencer keys,
        billing, and members all scope to it. Cross-workspace access is
        admin-only.
      </P>
      <H2>What's stored on a workspace</H2>
      <UL>
        <li>Display name + slug.</li>
        <li>Company website.</li>
        <li>Sequencer API keys (encrypted).</li>
        <li>Subscription status + Stripe customer / sub IDs.</li>
        <li>Trial end date.</li>
      </UL>
      <H2>Editing</H2>
      <P>
        <AppLink to="/settings">Settings</AppLink> → workspace name and
        contact email. Changes propagate immediately.
      </P>
      <H2>Multi-user workspaces</H2>
      <P>
        Right now one user owns one workspace. Multi-user workspaces
        (invite teammates) are on the roadmap; tell us if it's
        blocking you.
      </P>
    </>
  ),

  'account/trial-to-paid': () => (
    <>
      <P>
        Sendepot is a paid platform — three monthly tiers, all
        month-to-month, cancel any time. You'll pick a plan during
        signup; checkout is Stripe-hosted.
      </P>
      <H2>The plans</H2>
      <UL>
        <li><strong>Starter — $97/mo</strong> · up to 10 tenants.</li>
        <li><strong>Agency — $247/mo</strong> · up to 50 tenants.</li>
        <li><strong>Unlimited — $597/mo</strong> · unlimited tenants.</li>
      </UL>
      <H2>Where to manage billing</H2>
      <P>
        <AppLink to="/settings">Settings → Billing</AppLink>. Opens the
        Stripe portal where you can change plan, update payment method,
        or cancel.
      </P>
      <H2>If you cancel</H2>
      <P>
        Tenants you provisioned through Sendepot are accessible only via
        Sendepot — we rotate admin passwords during setup so the
        original CSP / source can't reach them. If you cancel, sending
        infrastructure stays online; you just can't create new tenants
        until you re-subscribe.
      </P>
      <P>
        Re-subscribing after cancellation: current pricing applies, plus
        3 months paid in advance (we re-do significant onboarding work
        when re-attaching tenants).
      </P>
    </>
  ),
};


// ── Renderer ─────────────────────────────────────────────────────────────

export function renderPage(section: string, page: string): ReactNode {
  const fn = PAGES[`${section}/${page}`];
  if (!fn) {
    return (
      <div className="text-sm text-dim">
        This page hasn't been written yet. Email{' '}
        <a className="text-accent hover:underline" href="mailto:support@sendepot.com">support@sendepot.com</a>{' '}
        if it's something you need.
      </div>
    );
  }
  return fn();
}
